import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Loader2, UserPlus } from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_orwion-manage/artifacts/mvxog26z_750x300beyaz.png";
const BG_IMAGE = "https://images.pexels.com/photos/1181318/pexels-photo-1181318.jpeg";

export function Register() {
    const navigate = useNavigate();
    const { register } = useAuth();
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        password: '',
        confirmPassword: '',
        phone: ''
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        if (formData.password !== formData.confirmPassword) {
            setError('Şifreler eşleşmiyor');
            return;
        }

        if (formData.password.length < 6) {
            setError('Şifre en az 6 karakter olmalıdır');
            return;
        }

        setLoading(true);

        try {
            await register({
                name: formData.name,
                email: formData.email,
                password: formData.password,
                phone: formData.phone,
                role: 'admin' // First user becomes admin for demo
            });
            navigate('/dashboard');
        } catch (err) {
            setError(err.response?.data?.detail || 'Kayıt olurken bir hata oluştu');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex">
            {/* Left Side */}
            <div className="hidden lg:flex lg:w-1/2 relative" style={{backgroundColor: '#1B3A57'}}>
                <div className="absolute inset-0 flex flex-col items-center justify-center p-12">
                    <img src={LOGO_URL} alt="Orwion" className="h-32 w-auto mb-12" />
                    <h1 className="font-heading text-4xl font-bold mb-4 text-white text-center">
                        Yeni Hesap Oluşturun
                    </h1>
                    <p className="text-lg text-white/80 max-w-md text-center">
                        Orwion Teknoloji iş yönetim portalına katılın ve tüm iş süreçlerinizi dijitalleştirin.
                    </p>
                </div>
            </div>

            {/* Right Side - Form */}
            <div className="flex-1 flex items-center justify-center p-6 bg-slate-50">
                <Card className="w-full max-w-md shadow-lg border-0">
                    <CardHeader className="text-center pb-2">
                        <div className="lg:hidden mb-8 flex justify-center">
                            <img src={LOGO_URL} alt="Orwion" className="h-20" />
                        </div>
                        <CardTitle className="font-heading text-2xl text-slate-800">Kayıt Ol</CardTitle>
                        <CardDescription className="text-slate-500">
                            Hesap bilgilerinizi girin
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="pt-4">
                        <form onSubmit={handleSubmit} className="space-y-4">
                            {error && (
                                <Alert variant="destructive">
                                    <AlertDescription>{error}</AlertDescription>
                                </Alert>
                            )}

                            <div className="space-y-2">
                                <Label htmlFor="name" className="text-slate-700">Ad Soyad</Label>
                                <Input
                                    id="name"
                                    name="name"
                                    type="text"
                                    placeholder="Adınız Soyadınız"
                                    value={formData.name}
                                    onChange={handleChange}
                                    required
                                    data-testid="register-name-input"
                                    className="h-11"
                                />
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="email" className="text-slate-700">E-posta</Label>
                                <Input
                                    id="email"
                                    name="email"
                                    type="email"
                                    placeholder="ornek@sirket.com"
                                    value={formData.email}
                                    onChange={handleChange}
                                    required
                                    data-testid="register-email-input"
                                    className="h-11"
                                />
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="phone" className="text-slate-700">Telefon (Opsiyonel)</Label>
                                <Input
                                    id="phone"
                                    name="phone"
                                    type="tel"
                                    placeholder="+90 5XX XXX XX XX"
                                    value={formData.phone}
                                    onChange={handleChange}
                                    data-testid="register-phone-input"
                                    className="h-11"
                                />
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="password" className="text-slate-700">Şifre</Label>
                                <Input
                                    id="password"
                                    name="password"
                                    type="password"
                                    placeholder="••••••••"
                                    value={formData.password}
                                    onChange={handleChange}
                                    required
                                    data-testid="register-password-input"
                                    className="h-11"
                                />
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="confirmPassword" className="text-slate-700">Şifre Tekrar</Label>
                                <Input
                                    id="confirmPassword"
                                    name="confirmPassword"
                                    type="password"
                                    placeholder="••••••••"
                                    value={formData.confirmPassword}
                                    onChange={handleChange}
                                    required
                                    data-testid="register-confirm-password-input"
                                    className="h-11"
                                />
                            </div>

                            <Button
                                type="submit"
                                className="w-full h-11 bg-orwion-green hover:bg-orwion-green-hover text-white font-medium"
                                disabled={loading}
                                data-testid="register-submit-btn"
                            >
                                {loading ? (
                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                ) : (
                                    <UserPlus className="w-4 h-4 mr-2" />
                                )}
                                Kayıt Ol
                            </Button>

                            <p className="text-center text-sm text-slate-500 pt-2">
                                Zaten hesabınız var mı?{' '}
                                <Link
                                    to="/login"
                                    className="text-orwion-green hover:underline font-medium"
                                    data-testid="login-link"
                                >
                                    Giriş Yapın
                                </Link>
                            </p>
                        </form>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
}

export default Register;
